import { useMemo } from "react";
import { RecordingType } from "../../types/general-types";

export type VideoEvent = {
	/**
	 * Time in seconds
	 */
	key: string;
	id?: number;
	time: number;
	name: string;
	type: string;
	details: Record<string, any>;
	flag_level: number;
	logged_time: string;
	resolved: boolean;
	comment?: string;
};
export type TimestampsArr = {
	/**
	 * Time in seconds
	 */
	start?: number;
	/**
	 * Time in seconds
	 */
	end?: number;
	vid: VideoEvent["details"][string];
}[];

export type SessionRecordingTimestamps = Record<RecordingType, Record<number, TimestampsArr>>;
export type RecordingPlaybackMedia = Partial<
	Record<RecordingType, Record<string | number, Record<string | number, { ok: boolean; durationMs?: number }>>>
>;

export const TIMESTAMP_PLACEHOLDER = { vid: "placeholder" };

export const getRecordingPlaybackStartOffset = (events: VideoEvent[]): number => {
	let assessmentStartTime: number | undefined;
	let firstRecordingStartTime: number | undefined;

	for (const event of events) {
		if (event.name === "start quiz") {
			assessmentStartTime = Math.min(event.time, assessmentStartTime ?? event.time);
		}

		if (event.name === "start recording" && event.details.success) {
			firstRecordingStartTime = Math.min(event.time, firstRecordingStartTime ?? event.time);
		}
	}

	const playbackStartTime =
		assessmentStartTime !== undefined &&
		(firstRecordingStartTime === undefined || firstRecordingStartTime <= assessmentStartTime)
			? assessmentStartTime
			: (firstRecordingStartTime ?? assessmentStartTime ?? 0);

	return Math.max(0, playbackStartTime * 1000);
};

export const getRecordingPlaybackEndOffset = ({
	media,
	timestamps,
}: {
	media: RecordingPlaybackMedia;
	timestamps: SessionRecordingTimestamps;
}): number | undefined => {
	let endOffsetMs: number | undefined;

	for (const recordingType of ["webcam", "screencapture"] satisfies RecordingType[]) {
		for (const sourceTimestamps of Object.values(timestamps[recordingType])) {
			for (const timestamp of sourceTimestamps) {
				if (timestamp.end !== undefined) {
					endOffsetMs = Math.max(endOffsetMs ?? 0, timestamp.end * 1000);
				}
			}
		}

		const mediaSources = media[recordingType] ?? {};
		for (const [sourceIndex, sourceSegments] of Object.entries(mediaSources)) {
			const sourceTimestamps = timestamps[recordingType][Number(sourceIndex)] ?? [];
			for (const [segmentId, segment] of Object.entries(sourceSegments)) {
				if (!segment.ok || typeof segment.durationMs !== "number") {
					continue;
				}
				const timestamp = sourceTimestamps.find(({ vid }) => String(vid) === String(segmentId));
				if (timestamp?.start === undefined) {
					continue;
				}
				endOffsetMs = Math.max(endOffsetMs ?? 0, timestamp.start * 1000 + segment.durationMs);
			}
		}
	}

	return endOffsetMs;
};

/** Spans of the session timeline that a probed, playable media segment actually covers. */
export type RecordedCoverage = { startMs: number; endMs: number }[];

/**
 * Where the playhead sits relative to the media that exists:
 * - `covered`: a segment covers this moment (or nothing has been probed yet, so we make no claim)
 * - `interior`: no media here, but a later segment exists — playback resumes further along
 * - `tail`: no media here and none after it — the recording stopped before the session did
 */
export type RecordedCoverageState = "covered" | "interior" | "tail";

/**
 * Real coverage for one recording source, from measured segment durations rather than the event
 * log's `end`. The two diverge whenever chunks stop being kept (an institution recording limit) or
 * stop arriving (sleep, crash, failed uploads), which is exactly what callers need to tell apart.
 */
export const getRecordedCoverage = ({
	segments,
	timestamps,
}: {
	segments?: Record<string | number, { ok: boolean; durationMs?: number }>;
	timestamps: TimestampsArr;
}): RecordedCoverage => {
	const coverage: RecordedCoverage = [];

	for (const [segmentId, segment] of Object.entries(segments ?? {})) {
		if (!segment.ok || typeof segment.durationMs !== "number") {
			continue;
		}
		const timestamp = timestamps.find(({ vid }) => String(vid) === String(segmentId));
		if (timestamp?.start === undefined) {
			continue;
		}
		const startMs = timestamp.start * 1000;
		coverage.push({ startMs, endMs: startMs + segment.durationMs });
	}

	return coverage.sort((a, b) => a.startMs - b.startMs);
};

/**
 * Classify a moment against a source's coverage. `toleranceMs` absorbs the sub-second disagreement
 * between a segment's event-logged start and its decoded duration, so ordinary playback near a
 * boundary is not reported as a gap.
 */
export const getRecordedCoverageState = (
	coverage: RecordedCoverage,
	timeMs: number,
	toleranceMs = 1000,
): RecordedCoverageState => {
	// Nothing probed yet (or every probe failed): the caller has no basis to claim a gap, and
	// guessing here would mislabel a still-processing recording as one that stopped early.
	if (coverage.length === 0) {
		return "covered";
	}
	if (coverage.some(({ startMs, endMs }) => timeMs >= startMs - toleranceMs && timeMs <= endMs + toleranceMs)) {
		return "covered";
	}
	return coverage.some(({ startMs }) => startMs > timeMs) ? "interior" : "tail";
};

/**
 * Length of the final covered span, i.e. the media produced by the *last* recording on this source.
 *
 * A recording limit is enforced as a chunk budget on one recording, and a MediaRecorder restart
 * mints a new recording with a fresh budget. So the quantity the limit bounds is this span alone —
 * summing the source's spans would let several short recordings add up past the limit and pass for
 * one that reached it. Chunks also accrue only while capture runs, so a span's own length (not its
 * distance from session start) is the right measure: an hour of sleep costs no budget.
 */
export const getLastRecordedSpanMs = (coverage: RecordedCoverage): number => {
	const lastSpan = coverage[coverage.length - 1];
	return lastSpan === undefined ? 0 : Math.max(0, lastSpan.endMs - lastSpan.startMs);
};

/**
 * Combine all elements of the array that have the same vid
 *
 * eg: [{vid: 1, start: 1}, {vid: 1, end: 2}] -> [{vid: 1, start: 1, end: 2}]
 */
const consolidateTimestampsArr = (timestamps: TimestampsArr): TimestampsArr => {
	const copiedTimestamps = timestamps.map((timestamp) => ({ ...timestamp }));
	const consolidatedTimestamps: TimestampsArr = [];
	const vidToTimestamp: Map<string, TimestampsArr[number]> = new Map();

	for (const currentTimestamp of copiedTimestamps) {
		const currentVid = String(currentTimestamp.vid);

		const existingTimestamp = vidToTimestamp.get(currentVid);
		if (existingTimestamp) {
			if (currentTimestamp.start && !existingTimestamp.start) {
				existingTimestamp.start = currentTimestamp.start;
			}
			if (currentTimestamp.end && !existingTimestamp.end) {
				existingTimestamp.end = currentTimestamp.end;
			}
		} else {
			vidToTimestamp.set(currentVid, currentTimestamp);
			consolidatedTimestamps.push(currentTimestamp);
		}
	}

	return consolidatedTimestamps;
};

// Does parsing of the timestamps on the front end.
// Could potentially combine this with the timestamp parsing we do on the node-server
export const useTimestamps = (events: VideoEvent[]): SessionRecordingTimestamps => {
	return useMemo(() => {
		const sessionTimestamps: SessionRecordingTimestamps = { webcam: {}, screencapture: {} };
		let assessmentStartTime: number | undefined;
		let hasMicrophoneEvents = false;
		if (events) {
			for (const event of events) {
				if (event.name === "start quiz") {
					assessmentStartTime = event.time;
				}

				const isStartRecording = event.name === "start recording";
				const isEndRecording = event.name === "end recording";
				if ((isStartRecording || isEndRecording) && event.details.success) {
					const sourceIndex: number = event.details.sourceIndex ?? 0;
					let recordingType: RecordingType;
					switch (event.details.media) {
						case "display":
							recordingType = "screencapture";
							break;
						case "microphone":
							hasMicrophoneEvents = true;
							recordingType = "webcam";
							break;
						case "webcam":
							if (hasMicrophoneEvents) {
								console.error("Both microphone and webcam events are found");
								continue;
							} else {
								recordingType = "webcam";
							}
							break;
						default:
							console.error("Invalid recording event media type", event.details.media);
							continue;
					}

					const sourceTimestamps = sessionTimestamps[recordingType][sourceIndex] ?? [{ ...TIMESTAMP_PLACEHOLDER }];
					sessionTimestamps[recordingType][sourceIndex] = sourceTimestamps;

					if (isStartRecording) {
						// This condition is erroneous
						if (
							sourceTimestamps.length > 1 &&
							sourceTimestamps[sourceTimestamps.length - 1]?.vid === event.details.video
						) {
							sourceTimestamps[sourceTimestamps.length - 1].start = event.time;
						} else {
							sourceTimestamps.push({ start: event.time, vid: event.details.video });
						}
					} else if (isEndRecording) {
						if (
							sourceTimestamps.length > 1 &&
							sourceTimestamps[sourceTimestamps.length - 1]?.vid === event.details.video
						) {
							sourceTimestamps[sourceTimestamps.length - 1].end = event.time;
						}
						// This condition is erroneous
						else {
							sourceTimestamps.push({ end: event.time, vid: event.details.video });
						}
					}
				}
			}
		}

		if (!assessmentStartTime) {
			return { webcam: {}, screencapture: {} };
		}

		return Object.fromEntries(
			Object.entries(sessionTimestamps).map(([type, sources]) => [
				type,
				Object.fromEntries(
					Object.entries(sources).map(([index, timestamps]) => [
						index,
						consolidateTimestampsArr(timestamps).filter(({ end }) => !end || end > assessmentStartTime!),
					]),
				),
			]),
		) as SessionRecordingTimestamps;
	}, [events]);
};

export default useTimestamps;
